import React, { useState, useEffect, useRef, useCallback } from "react";
import { Send, Loader2, MessagesSquare } from "lucide-react";
import { fetchChat, postChat } from "../api";
import { useAuth } from "../context/AuthContext";
import { Textarea } from "./ui/textarea";

function ts(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function ChatPanel({ scope, refId, refName, ownerId, compact = false }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => {
    if (!refId) return;
    fetchChat(scope, String(refId))
      .then((d) => setMessages(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scope, refId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const payload = {
        scope, refId: String(refId), refName: refName || "",
        userId: user.id, userName: user.name, userInitials: user.initials, userColor: user.color,
        text: text.trim(), ownerId: ownerId || null,
      };
      const msg = await postChat(payload);
      setMessages((m) => [...m, msg]);
      setText("");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="flex flex-col">
      <div className={`space-y-4 overflow-y-auto pr-1 ${compact ? "max-h-64" : "max-h-[420px] min-h-[160px]"}`}>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#d0333a]" /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-slate-400">
            <MessagesSquare className="h-7 w-7" />
            <p className="text-sm">No messages yet. Start the conversation.</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.userId === user.id;
            return (
              <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: m.userColor }}>{m.userInitials}</div>
                <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div className={`flex items-center gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                    <span className="text-xs font-semibold text-slate-700">{mine ? "You" : m.userName}</span>
                    <span className="text-[10px] text-slate-400">{ts(m.ts)}</span>
                  </div>
                  <div className={`mt-1 whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${mine ? "rounded-tr-sm bg-[#d0333a] text-white" : "rounded-tl-sm bg-slate-100 text-slate-800"}`}>{m.text}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-3 flex items-end gap-2 border-t border-slate-100 pt-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          rows={1}
          placeholder="Write a message... (Enter to send)"
          className="min-h-[40px] resize-none text-sm"
        />
        <button onClick={send} disabled={sending || !text.trim()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#d0333a] text-white transition-colors hover:bg-[#b02a30] disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
